import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { signInPath } from '../../utils/customDomain';
import { Card, CardContent } from '../ui/card';
import { Shield, Lock } from 'lucide-react';

const ProtectedRoute = ({ children, requiredRole = null }) => {
  const { isAuthenticated, user, loading, isAdmin, isEditor, isViewer } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={signInPath()} state={{ from: location }} replace />;
  }

  // Check role-based access
  if (requiredRole) {
    let hasAccess = false;
    
    switch (requiredRole) {
      case 'ADMIN':
        hasAccess = isAdmin();
        break;
      case 'EDITOR':
        hasAccess = isEditor();
        break;
      case 'VIEWER':
        hasAccess = isViewer();
        break;
      default:
        hasAccess = true;
    }

    if (!hasAccess) {
      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardContent className="p-8 text-center">
              <Lock className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
              <p className="text-gray-600 mb-4">
                You don't have permission to access this page. 
                Required role: <span className="font-semibold">{requiredRole}</span>
              </p>
              <p className="text-sm text-gray-500">
                Your current role: <span className="font-semibold">{user?.roles?.[0] || 'None'}</span>
              </p>
            </CardContent>
          </Card>
        </div>
      );
    }
  }

  return children;
};

export default ProtectedRoute;
